/**
 * Service Dashboard / wizard catalog mirror (grouped subservices and helpers).
 * Source of truth: `docs/cosmos-seeds/` → Cosmos ServiceTypes. Update seeds first, then sync this file.
 * Subservice `id` = seed/Cosmos `short_name`; trade `id` = service `short_name`.
 * Groups and per-sub icons are UI-only (not sent to the API).
 */
import { getTradeTheme, getTradeThemeFields } from '../tradeTheme';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BuildIcon from '@mui/icons-material/Build';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CountertopsIcon from '@mui/icons-material/Countertops';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PowerIcon from '@mui/icons-material/Power';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AirIcon from '@mui/icons-material/Air';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import BoltIcon from '@mui/icons-material/Bolt';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import EvStationIcon from '@mui/icons-material/EvStation';
import SensorsIcon from '@mui/icons-material/Sensors';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HearingIcon from '@mui/icons-material/Hearing';
import TuneIcon from '@mui/icons-material/Tune';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ParkIcon from '@mui/icons-material/Park';
import NatureIcon from '@mui/icons-material/Nature';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import YardIcon from '@mui/icons-material/Yard';
import BugReportIcon from '@mui/icons-material/BugReport';
import ForestIcon from '@mui/icons-material/Forest';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LayersIcon from '@mui/icons-material/Layers';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import LandscapeIcon from '@mui/icons-material/Landscape';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WaterIcon from '@mui/icons-material/Water';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import DeckIcon from '@mui/icons-material/Deck';
import FoundationIcon from '@mui/icons-material/Foundation';
import FireplaceIcon from '@mui/icons-material/Fireplace';
import HandymanIcon from '@mui/icons-material/Handyman';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import GrassIcon from '@mui/icons-material/Grass';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';

/** @typedef {{ id: string, label: string, icon: import('@mui/material').SvgIconComponent, description: string, urgent?: boolean }} CatalogSubservice */
/** @typedef {{ id: string, name: string, description: string, icon: import('@mui/material').SvgIconComponent, color: string, subservices: CatalogSubservice[] }} CatalogSubserviceGroup */
/** @typedef {{ id: string, name: string, icon: import('@mui/material').SvgIconComponent, color: string, description: string, groups: CatalogSubserviceGroup[], subserviceMode?: 'catalog' | 'businessDefined' }} CatalogTrade */

/** Trades where the business defines subservices (no catalog checklist). */
export function isBusinessDefinedTrade(trade) {
  if (!trade) return false;
  if (trade.subserviceMode === 'businessDefined') return true;
  const theme = getTradeTheme(trade.id);
  if (theme?.subserviceMode === 'businessDefined') return true;
  return !(trade.groups?.length);
}

/** @param {CatalogTrade | null | undefined} trade */
export function getTradeSubservicesFlat(trade) {
  if (!trade?.groups?.length) return [];
  return trade.groups.flatMap((g) => g.subservices ?? []);
}

/** @param {CatalogTrade | null | undefined} trade @param {string} subServiceTypeSlug */
export function findTradeSubserviceById(trade, subServiceTypeSlug) {
  if (!trade?.groups || !subServiceTypeSlug) return null;
  for (const group of trade.groups) {
    const found = (group.subservices ?? []).find(
      (s) => s.id === subServiceTypeSlug,
    );
    if (found) return found;
  }
  return null;
}

/** @param {CatalogTrade | null | undefined} trade @param {string} subServiceTypeSlug */
export function findTradeSubserviceWithGroup(trade, subServiceTypeSlug) {
  if (!trade?.groups || !subServiceTypeSlug) return null;
  for (const group of trade.groups) {
    const found = (group.subservices ?? []).find(
      (s) => s.id === subServiceTypeSlug,
    );
    if (found) return { subservice: found, group };
  }
  return null;
}

/** @param {string} subServiceTypeSlug */
export function findSubserviceAcrossTrades(subServiceTypeSlug) {
  if (!subServiceTypeSlug) return null;
  for (const trade of ALL_TRADES) {
    const subservice = findTradeSubserviceById(trade, subServiceTypeSlug);
    if (subservice) return { trade, subservice };
  }
  return null;
}

/** Public /services picker order (catalog trades only; excludes `other`). */
export const PUBLIC_SERVICE_PICKER_TRADE_IDS = [
  'landscaping',
  'plumbing',
  'hvac',
  'electrical',
];

/**
 * @param {string | null | undefined} tradeId
 * @returns {CatalogTrade | import('../tradeTheme').TradeTheme | null}
 */
export function getTradeById(tradeId) {
  if (!tradeId) return null;
  const id = String(tradeId).trim().toLowerCase();
  if (!id) return null;
  return ALL_TRADES.find((t) => t.id === id) ?? getTradeTheme(id) ?? null;
}

/** @returns {CatalogTrade[]} */
export function getPublicServicePickerTrades() {
  return PUBLIC_SERVICE_PICKER_TRADE_IDS.map((id) => getTradeById(id)).filter(
    (trade) => trade && !isBusinessDefinedTrade(trade),
  );
}

/**
 * Human-readable trade name for UI.
 * @param {string | null | undefined} tradeId
 */
export function getTradeDisplayName(tradeId) {
  const trade = getTradeById(tradeId);
  if (trade?.name) return trade.name;
  const slug = typeof tradeId === 'string' ? tradeId.trim() : '';
  return slug || '—';
}

/** @type {CatalogTrade[]} */
export const ALL_TRADES = [
  {
    ...getTradeThemeFields('plumbing'),
    groups: [
      {
        id: 'plumbing-leaks-clogs',
        name: 'Leaks and Clogs',
        description:
          'Slow or blocked drains, dripping pipes and supply lines, and toilet problems—snaking, leaks at fixtures, and repairs when things won’t flow or flush right.',
        icon: OpacityIcon,
        color: '#0369a1',
        subservices: [
          {
            id: 'clogged-drain',
            label: 'Drain or Sink Is Clogged',
            icon: OpacityIcon,
            description:
              'Slow or blocked kitchen, bath, floor, or main drains; snaking and clearing.',
          },
          {
            id: 'leaking-pipe',
            label: 'Pipe or Faucet Is Leaking',
            icon: WaterDropIcon,
            description:
              'Repair of leaking pipes, faucets, supply lines, hose bibs, and visible drips.',
          },
          {
            id: 'toilet-issue',
            label: 'Toilet Is Running, Leaking, or Not Flushing',
            icon: BuildIcon,
            description:
              'Toilet repairs including running, leaks at base or tank, weak flush, and blockages.',
          },
        ],
      },
      {
        id: 'plumbing-disposals',
        name: 'Disposals',
        description:
          'Garbage disposal jam, repair, replacement, or new installation under the sink.',
        icon: DeleteOutlineIcon,
        color: '#0284c7',
        subservices: [
          {
            id: 'garbage-disposal',
            label: 'Garbage Disposal Not Working',
            icon: DeleteOutlineIcon,
            description:
              'Garbage disposal jam repair, replacement, or new install.',
          },
        ],
      },
      {
        id: 'plumbing-installations',
        name: 'Installations',
        description:
          'New or replacement fixtures and water heaters—tank, tankless, or heat pump.',
        icon: CountertopsIcon,
        color: '#0d4efe',
        subservices: [
          {
            id: 'water-heater',
            label: 'Water Heater Help',
            icon: WhatshotIcon,
            description:
              'Water heater repair, replacement, or new installation (tank, tankless, or heat pump).',
          },
          {
            id: 'faucet-install',
            label: 'Install or Replace Faucet / Sink',
            icon: CountertopsIcon,
            description:
              'Faucet and sink installation or replacement in kitchen, bath, or laundry.',
          },
        ],
      },
    ],
  },
  {
    ...getTradeThemeFields('electrical'),
    groups: [
      {
        id: 'electrical-installation',
        name: 'Installation & upgrades',
        description:
          'New devices, circuits, panels, EV charging, and safety equipment—installed to code.',
        icon: HandymanIcon,
        color: '#d97706',
        subservices: [
          {
            id: 'install-outlet',
            label: 'Install or Add an Electrical Outlet',
            icon: PowerIcon,
            description:
              'Adding or replacing standard or GFCI outlets where more power access is needed.',
          },
          {
            id: 'install-ceiling-fan',
            label: 'Install a Ceiling Fan or Light Fixture',
            icon: AirIcon,
            description:
              'Mounting and wiring ceiling fans, chandeliers, and light fixtures.',
          },
          {
            id: 'install-switch-dimmer',
            label: 'Install or Replace a Light Switch or Dimmer',
            icon: AddCircleOutlineIcon,
            description: 'New switches, dimmers, and multi-way switching.',
          },
          {
            id: 'install-outdoor-lighting',
            label: 'Install Outdoor or Security Lighting',
            icon: WbTwilightIcon,
            description:
              'Porch, soffit, motion, flood, and landscape electrical lighting.',
          },
          {
            id: 'install-dedicated-circuit',
            label: 'Install a Dedicated Circuit for Appliance',
            icon: BoltIcon,
            description:
              'Dedicated circuits for kitchens, laundry, workshops, or large appliances.',
          },
          {
            id: 'panel-upgrade',
            label: 'Electrical Panel Upgrade or Replacement',
            icon: DashboardCustomizeIcon,
            description:
              'Service panel replacement, capacity upgrades, and breaker work.',
          },
          {
            id: 'ev-charger',
            label: 'EV Charger Installation',
            icon: EvStationIcon,
            description: 'Home EV charging station wiring and installation.',
          },
          {
            id: 'smoke-co-detector',
            label: 'Smoke or Carbon Monoxide Detector Install',
            icon: SensorsIcon,
            description:
              'Hardwired or interconnected smoke and CO detector installation.',
          },
          {
            id: 'generator-install',
            label: 'Generator Installation',
            icon: OfflineBoltIcon,
            description:
              'Standby or portable backup generator installation, including transfer switch wiring and hookup.',
          },
        ],
      },
      {
        id: 'electrical-troubleshooting',
        name: 'Troubleshooting & repairs',
        description:
          'When something is not working—outlets, switches, breakers, partial power, or flickering lights.',
        icon: BuildIcon,
        color: '#ea580c',
        subservices: [
          {
            id: 'outlet-not-working',
            label: 'Outlet Not Working or Sparking',
            icon: ErrorOutlineIcon,
            description:
              'Dead outlets, loose connections, sparking, or burn marks.',
          },
          {
            id: 'light-switch-not-working',
            label: 'Light Switch Not Working',
            icon: ToggleOffIcon,
            description:
              'Switches that do not control lights or need replacement.',
          },
          {
            id: 'breaker-tripping',
            label: 'Breaker Keeps Tripping',
            icon: FlashOnIcon,
            description:
              'Repeated breaker trips, nuisance tripping, and overload diagnosis.',
          },
          {
            id: 'power-out-partial',
            label: 'Power Out in Part of the House',
            icon: HomeWorkIcon,
            description:
              'Partial loss of power to rooms or circuits while the rest of the home has power.',
          },
          {
            id: 'lights-flickering',
            label: 'Lights Flickering or Dimming',
            icon: LightbulbIcon,
            description:
              'Flickering or dimming lights that may indicate loose connections or supply issues.',
          },
        ],
      },
      {
        id: 'electrical-safety',
        name: 'Safety & assessment',
        description:
          'Whole-home or focused reviews of your electrical system for peace of mind and insurance needs.',
        icon: SecurityIcon,
        color: '#b45309',
        subservices: [
          {
            id: 'safety-inspection',
            label: 'Home Electrical Safety Inspection',
            icon: SecurityIcon,
            description:
              'Whole-home review of wiring, devices, and panels for safety and code concerns.',
          },
          {
            id: 'panel-assessment',
            label: 'Electrical Panel Assessment',
            icon: AssessmentIcon,
            description:
              'Focused evaluation of the main or subpanel for age, capacity, and condition.',
          },
        ],
      },
    ],
  },
  {
    ...getTradeThemeFields('hvac'),
    groups: [
      {
        id: 'hvac-comfort-issues',
        name: 'Comfort & performance issues',
        description:
          'AC, heat, airflow, odors, and unusual sounds—diagnosis and repair when the system feels “off.”',
        icon: AcUnitIcon,
        color: '#0891b2',
        subservices: [
          {
            id: 'ac-not-cooling',
            label: "My AC isn't cooling",
            icon: AcUnitIcon,
            description:
              'Air conditioning that runs but does not cool, short-cycling, freezing up, or water leaks.',
          },
          {
            id: 'heat-not-working',
            label: "My heat isn't working",
            icon: WhatshotIcon,
            description:
              'Furnace or heat pump not heating, blowing cold air, or not starting.',
          },
          {
            id: 'smell-sound',
            label: 'Something smells or sounds wrong',
            icon: HearingIcon,
            description:
              'Burning, musty, or sulfur odors; banging, squealing, rattling, or clicking from equipment.',
          },
          {
            id: 'airflow-vents',
            label: 'My airflow or vents seem off',
            icon: AirIcon,
            description:
              'Weak or no airflow, uneven temperatures between rooms, or duct and vent concerns.',
          },
        ],
      },
      {
        id: 'hvac-maintenance',
        name: 'Maintenance',
        description:
          'Seasonal tune-ups and checkups to extend equipment life and catch issues early.',
        icon: TuneIcon,
        color: '#0e7490',
        subservices: [
          {
            id: 'tune-up',
            label: 'Schedule a tune-up or maintenance visit',
            icon: TuneIcon,
            description:
              'Seasonal or annual maintenance for cooling, heating, or both.',
          },
        ],
      },
      {
        id: 'hvac-installation',
        name: 'New equipment & controls',
        description:
          'Thermostat upgrades and full replacements for AC, furnace, or heat pump systems.',
        icon: AddCircleOutlineIcon,
        color: '#155e75',
        subservices: [
          {
            id: 'thermostat-install',
            label: 'Install or replace a thermostat',
            icon: ThermostatIcon,
            description:
              'Thermostat replacement, new install, or upgrade to smart or programmable models.',
          },
          {
            id: 'ac-install',
            label: 'Install a new AC unit',
            icon: AddCircleOutlineIcon,
            description:
              'New central AC, ductless mini-splits, or other cooling system installation.',
          },
          {
            id: 'furnace-install',
            label: 'Install a new furnace or heating system',
            icon: LocalFireDepartmentIcon,
            description:
              'New furnace, heat pump, or heating system installation or replacement.',
          },
        ],
      },
    ],
  },
  {
    ...getTradeThemeFields('landscaping'),
    groups: [
      {
        id: 'landscaping-lawn-turf',
        name: 'Lawn & turf care',
        description:
          'Mowing, aeration, seeding, sod, fertilization, weeds, and lawn pest control.',
        icon: GrassIcon,
        color: '#15803d',
        subservices: [
          {
            id: 'mow-edge-trim',
            label: 'Mow, edge & trim (lawn & walkways)',
            icon: ContentCutIcon,
            description:
              'Regular mowing plus edging and trimming along lawn edges and walkways.',
          },
          {
            id: 'aerate-lawn',
            label: 'Aerate My Lawn',
            icon: YardIcon,
            description:
              'Core aeration to reduce compaction and improve water and nutrient uptake.',
          },
          {
            id: 'overseed-reseed',
            label: 'Overseed or Reseed Bare Spots',
            icon: AgricultureIcon,
            description:
              'Overseeding or spot seeding to thicken turf and fill thin or bare areas.',
          },
          {
            id: 'new-sod-turf',
            label: 'Install New Sod or Turf',
            icon: GrassIcon,
            description:
              'New sod or turf installation for an instant lawn or repair areas.',
          },
          {
            id: 'fertilize-lawn',
            label: 'Fertilize My Lawn',
            icon: LocalFloristIcon,
            description:
              'Lawn fertilization tailored to grass type and season.',
          },
          {
            id: 'weed-control',
            label: 'Weed Control or Treatment',
            icon: NatureIcon,
            description:
              'Selective herbicide or treatment programs for broadleaf and grassy weeds.',
          },
          {
            id: 'pest-grub',
            label: 'Pest or Grub Control',
            icon: BugReportIcon,
            description:
              'Treatment for lawn pests, grubs, and other turf-damaging insects.',
          },
        ],
      },
      {
        id: 'landscaping-trees-shrubs',
        name: 'Trees & shrubs',
        description:
          'Pruning, removal, stump work, and new plantings for structure and shade.',
        icon: ParkIcon,
        color: '#166534',
        subservices: [
          {
            id: 'trim-prune-shrubs',
            label: 'Trim or Prune Shrubs / Bushes',
            icon: ContentCutIcon,
            description:
              'Shaping, pruning, and health trimming for shrubs and ornamental bushes.',
          },
          {
            id: 'tree-trimming',
            label: 'Tree Trimming or Branch Removal',
            icon: ParkIcon,
            description:
              'Canopy trimming, deadwood removal, and clearance pruning for safety and aesthetics.',
          },
          {
            id: 'remove-tree-stump',
            label: 'Remove a Tree or Stump',
            icon: ForestIcon,
            description: 'Tree removal, stump grinding, and site cleanup.',
          },
          {
            id: 'plant-trees-shrubs',
            label: 'Plant Trees or Shrubs',
            icon: ParkIcon,
            description:
              'Selection, planting, and establishment of new trees and shrubs.',
          },
        ],
      },
      {
        id: 'landscaping-cleanup-beds',
        name: 'Cleanup & garden beds',
        description:
          'Seasonal cleanup, mulch, flowers, edging, and decorative stone for tidy beds.',
        icon: LocalFloristIcon,
        color: '#15803d',
        subservices: [
          {
            id: 'leaf-removal',
            label: 'Leaf Removal or Cleanup',
            icon: CleaningServicesIcon,
            description: 'Seasonal leaf collection, bagging, and haul-away.',
          },
          {
            id: 'debris-brush',
            label: 'Remove Debris, Brush, or Branches',
            icon: DeleteSweepIcon,
            description:
              'Clearing yard debris, brush piles, and fallen branches.',
          },
          {
            id: 'mulch-beds',
            label: 'Install or Refresh Mulch in Beds',
            icon: LayersIcon,
            description:
              'Mulch delivery and installation or refresh for landscape beds.',
          },
          {
            id: 'flower-beds',
            label: 'Plant or Redesign Flower Beds',
            icon: LocalFloristIcon,
            description:
              'Bed design, seasonal color, and perennial or annual plantings.',
          },
          {
            id: 'garden-edging',
            label: 'Install Garden Borders or Edging',
            icon: BorderAllIcon,
            description:
              'Metal, stone, or plastic edging to define beds and paths.',
          },
          {
            id: 'decorative-rock',
            label: 'Install Decorative Rock or Stone',
            icon: LandscapeIcon,
            description:
              'Decorative gravel, river rock, or stone ground cover in beds or paths.',
          },
        ],
      },
      {
        id: 'landscaping-grading-drainage',
        name: 'Grading & drainage',
        description:
          'Surface grading and drainage solutions to move water away from structures.',
        icon: TrendingFlatIcon,
        color: '#14532d',
        subservices: [
          {
            id: 'grade-level',
            label: 'Grade or Level Yard / Fix Low Spots',
            icon: TrendingFlatIcon,
            description:
              'Rough grading, filling low spots, and improving surface drainage on soil.',
          },
          {
            id: 'drainage',
            label: 'Install or Fix Drainage (French Drain, Downspout)',
            icon: WaterIcon,
            description:
              'French drains, catch basins, downspout extensions, and yard drainage fixes.',
          },
        ],
      },
      {
        id: 'landscaping-irrigation',
        name: 'Irrigation',
        description:
          'Sprinkler and drip systems—new installs, repairs, and seasonal startup or winterization.',
        icon: WaterDropIcon,
        color: '#0d9488',
        subservices: [
          {
            id: 'install-sprinkler',
            label: 'Install a Sprinkler or Irrigation System',
            icon: WaterDropIcon,
            description:
              'Design and installation of sprinkler or drip irrigation systems.',
          },
          {
            id: 'repair-sprinkler',
            label: 'Repair or Adjust Sprinkler Heads',
            icon: BuildIcon,
            description:
              'Repairs to heads, valves, lines, and controller troubleshooting.',
          },
          {
            id: 'winterization',
            label: 'Sprinkler System Winterization (Blowout)',
            icon: AcUnitOutlinedIcon,
            description:
              'Fall blowout and winterization to protect pipes from freezing.',
          },
          {
            id: 'spring-irrigation',
            label: 'Spring Irrigation Startup',
            icon: WbSunnyIcon,
            description:
              'Spring turn-on, inspection, and adjustment of the irrigation system.',
          },
        ],
      },
      {
        id: 'landscaping-hardscape',
        name: 'Hardscaping & outdoor living',
        description:
          'Patios, walls, lighting, fire features, and shade structures for usable outdoor space.',
        icon: DeckIcon,
        color: '#ca8a04',
        subservices: [
          {
            id: 'patio-walkway',
            label: 'Install a Patio or Walkway',
            icon: DeckIcon,
            description:
              'Pavers, concrete, stone, or similar patio and walkway construction.',
          },
          {
            id: 'retaining-wall',
            label: 'Build or Repair a Retaining Wall',
            icon: FoundationIcon,
            description:
              'Structural or decorative retaining walls for grade changes.',
          },
          {
            id: 'landscape-lighting',
            label: 'Install Outdoor / Landscape Lighting',
            icon: LightbulbIcon,
            description:
              'Path, accent, security, and low-voltage landscape lighting.',
          },
          {
            id: 'fire-pit',
            label: 'Build a Fire Pit Area',
            icon: FireplaceIcon,
            description:
              'Fire pit installation or built-in outdoor fire features.',
          },
          {
            id: 'pergola-shade',
            label: 'Install a Pergola or Shade Structure',
            icon: DeckIcon,
            description:
              'Pergolas, arbors, and shade sails for patios and outdoor living.',
          },
        ],
      },
    ],
  },
  {
    ...getTradeThemeFields('other'),
    groups: [],
  },
];

export {
  getTradeTheme,
  getTradeThemeFields,
  getTradeThemeList,
  getTradeIconChipStyle,
  getTradeColorClassName,
  TRADE_THEMES,
  TRADE_THEME_ORDER,
} from '../tradeTheme';
